import React, { useMemo } from 'react';
import { startCase } from 'lodash';

import SecuredPage from 'Views/Components/Security/SecuredPage';
import Navbar from 'Views/Components/Navbar/Navbar';
import Tabs from 'Views/Components/Tabs/Tabs';
import StaffListWithFilters from 'Views/Pages/Frontend/UserManagement/StaffListWithFilters';
import StudentListWithFilters from 'Views/Pages/Frontend/UserManagement/StudentListWithFilters';
import { useCountUserTypes } from 'Queries/Hooks/Accounts/useCountUserTypes';
import { hasAnyRole } from 'Util/SecurityUtils';
import { useHistory, useLocation } from 'react-router';
import useQueryParams from 'Hooks/useQueryParams';
import useStore from 'Hooks/useStore';

export const tabs = [
	'staff',
	'students',
] as const;

const UserManagementPage = () => {
	const userCountData = useCountUserTypes();
	const location = useLocation();
	const history = useHistory();
	const store = useStore();

	const queryParams = useQueryParams();

	const initialTabIndex = useMemo(
		() => {
			const isKnownTab = (maybeTab: string | null): maybeTab is typeof tabs[number] => {
				return tabs.includes(maybeTab as typeof tabs[number]);
			};

			if (queryParams.has('tab')) {
				const tabParam = queryParams.get('tab');
				if (isKnownTab(tabParam)) {
					return tabs.indexOf(tabParam) || 0;
				}
			}
			return 0;
		},
		[queryParams],
	);

	const [staffTotal, studentTotal] = useMemo<[number | undefined, number | undefined]>(
		() => {
			if (!userCountData.data) {
				return [undefined, undefined];
			}
			const {
				countUserEntitys, countAdminEntitys, countCurriculumSupportEntitys,
				countInstitutionAdminEntitys, countStudentAdvisorEntitys,
			} = userCountData.data.data;

			const staffTypes = [
				countAdminEntitys, countCurriculumSupportEntitys,
				countInstitutionAdminEntitys, countStudentAdvisorEntitys,
			];

			const staffUserTypeTotal = staffTypes.reduce((acc, curr) => acc + curr.number, 0);

			return [staffUserTypeTotal, countUserEntitys.number];
		},
		[userCountData.data],
	);

	const staffCountLabel = staffTotal !== undefined
		? <span className="user-count-label">{staffTotal.toLocaleString()}</span>
		: '';

	const studentCountLabel = studentTotal !== undefined
		? <span className="user-count-label">{studentTotal.toLocaleString()}</span>
		: '';

	return (
		<SecuredPage
			groups={[
				'Super Administrators',
				'Admin',
				'InstitutionAdmin',
				'StudentAdvisor',
				'CurriculumSupport',
			]}
		>
			<div className="body-content">
				<Navbar />
				<main className="user-management-page">
					<div className="row">
						<div className="title">
							<h4>Users</h4>
						</div>
					</div>
					<Tabs
						innerProps={{ 'aria-label': 'user roles tab' }}
						defaultTab={initialTabIndex}
						tabs={[
							...hasAnyRole(store, ['Admin', 'InstitutionAdmin', 'StudentAdvisor', 'CurriculumSupport'])
								? [{
									key: tabs[0],
									name: <span data-testid="staff-tab">{startCase(tabs[0])}{staffCountLabel}</span>,
									component: <StaffListWithFilters />,
								}]
								: [],
							...hasAnyRole(store, ['Admin', 'InstitutionAdmin', 'StudentAdvisor'])
								? [{
									key: tabs[1],
									name: <span data-testid="user-tab">{startCase(tabs[1])}{studentCountLabel}</span>,
									component: <StudentListWithFilters />,
								}]
								: [],
						]}
						onTabClicked={newTabIndex => {
							// set tab param and update in browser history
							queryParams.set('tab', tabs[newTabIndex]);
							history.replace({
								pathname: location.pathname,
								search: `?${queryParams.toString()}`,
							});
						}}
					/>
				</main>
			</div>
		</SecuredPage>
	);
};

export default UserManagementPage;
